import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AgeRangeSelect } from '../../../components/age-range-select';
import { BulkEditModal } from '../../../components/bulk-edit-modal';
import { usePermissions } from '../../../hooks/usePermissions';
import { getPlansList } from '../../../services/plans/listPlans';
import { updateGrid } from '../../../services/plans/updateGrid';
import { InsurancePlan } from '../../../types/plans/types';
import {
  coerceRangeForBackend,
  labelToRange,
  rangeToLabel,
  reconstructAgeRange,
} from '../../../utils/ageRanges';
import { getStatusConfig } from '../../../utils/getStatusConfig';
import { RESOURCE_GRID_COPY } from '../../../utils/resourceGridCopy';

interface BulkEditState {
  combinedStatus?: string; // Combined status like ProviderInsuranceTable
  ageRange?: string;
}

// Single combined dropdown options as requested by client (same as ProviderInsuranceTable)
const COMBINED_STATUS_OPTIONS = [
  'Schedule Allowed',
  'Pending - Do Not Schedule',
  'Not Participating',
  'Established Patients Only',
  'PCP Match Required',
  'No Status',
];

// Helper function to map combined status to credential status and value
const getCombinedStatusMapping = (combinedStatus: string) => {
  switch (combinedStatus) {
    case 'Schedule Allowed':
      return {
        credentialStatus: 'Credentialed',
        value: 'Schedule Allowed',
      };
    case 'Pending - Do Not Schedule':
      return { credentialStatus: 'Pending', value: 'No Status' };
    case 'Not Participating':
      return {
        credentialStatus: 'Credentialed',
        value: 'Not Participating',
      };
    case 'Established Patients Only':
      return {
        credentialStatus: 'Credentialed',
        value: 'Established Patients',
      };
    case 'PCP Match Required':
      return { credentialStatus: 'Credentialed', value: 'PCP Match' };
    case 'No Status':
      return { credentialStatus: 'Credentialed', value: 'No Status' };
    default:
      return { credentialStatus: 'Credentialed', value: 'No Status' };
  }
};

// Helper function to get combined status from credential status and value
const getCombinedStatusFromFields = (
  credentialStatus: string,
  value: string
) => {
  if (credentialStatus?.toLowerCase() === 'pending') {
    return 'Pending - Do Not Schedule';
  }

  // Handle case-insensitive matching for value
  const valueLower = value?.toLowerCase();

  switch (valueLower) {
    case 'schedule allowed':
      return 'Schedule Allowed';
    case 'not participating':
      return 'Not Participating';
    case 'established patients':
      return 'Established Patients Only';
    case 'pcp match':
      return 'PCP Match Required';
    case 'no status':
      return 'No Status';
    default:
      return 'No Status';
  }
};

// Helper function to get status config for dropdown options
const getStatusConfigForOption = (combinedStatus: string) => {
  const { credentialStatus, value } =
    getCombinedStatusMapping(combinedStatus);
  return getStatusConfig(
    value,
    undefined,
    undefined,
    credentialStatus
  );
};

const BulkEditPlans: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { canEditInsurance, isAdmin } = usePermissions();

  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const { isOpen: isPlanSelectionOpen, onOpen: onPlanSelectionOpen, onClose: onPlanSelectionClose } = useDisclosure();

  // Get context from URL params
  const from = searchParams.get('from');

  // Extract filters from URL params
  const filters: Record<string, string | string[]> = useMemo(() => {
    const filterObj: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_')) {
        const filterKey = key.replace('filter_', '');
        if (filterObj[filterKey]) {
          // Handle multiple values (arrays)
          if (Array.isArray(filterObj[filterKey])) {
            (filterObj[filterKey] as string[]).push(value);
          } else {
            filterObj[filterKey] = [filterObj[filterKey] as string, value];
          }
        } else {
          filterObj[filterKey] = value;
        }
      }
    });
    return filterObj;
  }, [searchParams]);

  // State management
  const [plans, setPlans] = useState<InsurancePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [bulkEditState, setBulkEditState] = useState<BulkEditState>({});
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [, setResults] = useState<{ success: string[]; failures: { id: string; error: string }[] }>({
    success: [],
    failures: [],
  });

  // Load plans based on filters
  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPlansList('', filters, 1000); // Large limit for bulk edit
      setPlans(response.data?.insurancePlans || []);

      // Pre-select all rows
      const allIds = new Set((response.data?.insurancePlans || []).map((plan: InsurancePlan) =>
        `${plan.provider.id}-${plan.practice.id}`
      ));
      setSelectedRows(allIds);
    } catch (error) {
      toast({
        title: 'Failed to load plans',
        description: 'Could not load insurance plans for bulk editing.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  // Get plan name from plans data or URL params
  const planName = useMemo(() => {
    // Try to get plan name from URL params first
    const planNameParam = searchParams.get('planName');
    if (planNameParam) {
      return decodeURIComponent(planNameParam);
    }
    // Otherwise, get from first plan in the list
    if (plans.length > 0 && plans[0].plans.length > 0) {
      return plans[0].plans[0].insurance?.friendlyName || plans[0].plans[0].insurance?.companyName || 'Unknown Plan';
    }
    return 'Unknown Plan';
  }, [plans, searchParams]);

  useEffect(() => {
    if (from === 'resource-grid' || from === 'planDetails') {
      loadPlans();
    } else if (from) {
      // If from parameter exists but is not recognized, still try to load
      loadPlans();
    } else {
      // Redirect if no from parameter
      navigate('/plans');
    }
  }, [from, loadPlans, navigate]);

  // Handle row selection
  const toggleRow = (rowId: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedRows(newSelected);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === plans.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(
        new Set(
          plans.map(
            (plan) => `${plan.provider.id}-${plan.practice.id}`
          )
        )
      );
    }
  };

  // Handle plan selection from modal
  const handlePlanSelectionConfirm = (planIds: string[]) => {
    setSelectedPlanIds(planIds);
    onPlanSelectionClose();
    // Proceed to the actual bulk edit interface
    handlePlanSelectionComplete();
  };

  // Handle bulk edit state changes
  const updateBulkEditState = (
    field: keyof BulkEditState,
    value: string | boolean
  ) => {
    setBulkEditState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Process bulk updates - first show plan selection modal
  const processBulkUpdate = async () => {
    if (selectedRows.size === 0) {
      toast({
        title: 'No rows selected',
        description: RESOURCE_GRID_COPY.bulkEditor.noRowsSelected,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (Object.keys(bulkEditState).length === 0) {
      toast({
        title: 'No changes selected',
        description: RESOURCE_GRID_COPY.bulkEditor.noChangesSelected,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Open plan selection modal first
    onPlanSelectionOpen();
  };

  // Execute actual bulk update after plan selection
  const handlePlanSelectionComplete = async () => {
    if (selectedPlanIds.length === 0) {
      toast({
        title: 'No plans selected',
        description:
          'Please select at least one insurance plan to update.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onConfirmOpen();
  };

  // Perform the actual bulk update operation
  const performBulkUpdate = async () => {
    setProcessing(true);

    const success: string[] = [];
    const failures: { id: string; error: string }[] = [];

    // Filter selected provider-practice combinations
    const selectedPlans = plans.filter((plan) =>
      selectedRows.has(`${plan.provider.id}-${plan.practice.id}`)
    );

    // Build list of all plan updates to perform
    const updates: Array<{
      plan: InsurancePlan;
      planRow: { id: string; plan_id: string };
      providerName: string;
      practiceName: string;
    }> = [];

    selectedPlans.forEach((plan) => {
      // Filter plans by selectedPlanIds if any are selected, otherwise update all plans
      const plansToUpdate =
        selectedPlanIds.length > 0
          ? plan.plans.filter((p) =>
              selectedPlanIds.includes(p.insurance.id)
            )
          : plan.plans;

      plansToUpdate.forEach((planRow) => {
        updates.push({
          plan,
          planRow: {
            id: planRow.id,
            plan_id: planRow.insurance.id,
          },
          providerName: `${plan.provider.firstName} ${plan.provider.lastName}`,
          practiceName: plan.practice.name || 'Unknown Practice',
        });
      });
    });

    setProgress({ current: 0, total: updates.length });

    // Process in chunks of 25
    const chunkSize = 25;
    for (let i = 0; i < updates.length; i += chunkSize) {
      const chunk = updates.slice(i, i + chunkSize);

      // Process chunk in parallel
      const chunkPromises = chunk.map(async (update) => {
        try {
          const { plan, planRow, providerName, practiceName } =
            update;

          // Prepare age range
          let minimumAge: number | null = null;
          let maximumAge: number | null = null;
          if (bulkEditState.ageRange) {
            const ageRange = labelToRange(bulkEditState.ageRange);
            const coerced = coerceRangeForBackend(ageRange);
            minimumAge = coerced.minimumAge;
            maximumAge = coerced.maximumAge;
          }

          // Prepare credential status and value - match ProviderInsuranceTable logic exactly
          // Use combined status if provided, otherwise use existing plan values
          const existingPlan = plan.plans.find((p) => p.id === planRow.id);

          let credentialStatus: string;
          let value: string;

          if (bulkEditState.combinedStatus) {
            // Use combined status mapping (same as ProviderInsuranceTable)
            const mapping = getCombinedStatusMapping(bulkEditState.combinedStatus);
            credentialStatus = mapping.credentialStatus;
            value = mapping.value;
          } else {
            // Use existing plan values if no bulk edit status is set
            credentialStatus = existingPlan?.credentialstatus || 'Credentialed';
            value = existingPlan?.value || 'No Status';
          }

          // Call update API
          await updateGrid({
            id: planRow.id,
            practiceId: plan.practice.id,
            providerId: plan.provider.id,
            plan_id: planRow.plan_id,
            credentialStatus,
            value,
            minimumAge,
            maximumAge,
          });

          success.push(`${providerName} - ${practiceName}`);
          return { success: true, update };
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          failures.push({
            id: `${update.plan.provider.id}-${update.plan.practice.id}-${update.planRow.plan_id}`,
            error: errorMessage,
          });
          return { success: false, update };
        }
      });

      await Promise.all(chunkPromises);
      setProgress({
        current: Math.min(i + chunk.length, updates.length),
        total: updates.length,
      });
    }

    setResults({ success, failures });
    setProcessing(false);
    onConfirmClose();

    // Show results
    toast({
      title: RESOURCE_GRID_COPY.bulkEditor.completed
        .replace('{successCount}', success.length.toString())
        .replace('{failureCount}', failures.length.toString()),
      status: failures.length === 0 ? 'success' : 'warning',
      duration: 8000,
      isClosable: true,
    });

    // Reload plans to reflect changes
    await loadPlans();
  };

  // Generate filter summary
  const getFilterSummary = () => {
    const parts = [];
    if (filters.marketId) parts.push(`Market: ${filters.marketId}`);
    if (filters.insuranceIds) {
      // Show plan name instead of plan ID
      parts.push(`Plan: ${planName}`);
    }
    if (filters.practiceIds) parts.push(`Practice: ${filters.practiceIds}`);
    if (filters.providerIds) parts.push(`Provider: ${filters.providerIds}`);
    if (filters.search) parts.push(`Search: ${filters.search}`);

    return parts.length > 0 ? parts.join(', ') : 'All plans';
  };

  if (!canEditInsurance() && !isAdmin()) {
    return (
      <Box p="2rem" textAlign="center">
        <Text fontSize="xl" color="red.500">
          You don't have permission to edit insurance plans.
        </Text>
        <Button mt="1rem" onClick={() => navigate('/plans')}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box p="2rem" maxW="1400px" mx="auto">
      {/* Header */}
      <Flex justify="space-between" align="center" mb="2rem">
        <HStack spacing="1rem">
          <Button
            leftIcon={<ChevronLeftIcon />}
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <VStack align="start" spacing="0.25rem">
            <Text fontSize="2xl" fontWeight="bold">
              {RESOURCE_GRID_COPY.bulkEditor.title}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {RESOURCE_GRID_COPY.bulkEditor.summary
                .replace('{filters}', getFilterSummary())
                .replace('{count}', plans.length.toString())}
            </Text>
          </VStack>
        </HStack>
      </Flex>

      <Stack spacing="2rem">
        {/* Changes to Apply */}
        <Box
          p="1.5rem"
          bg="white"
          borderRadius="0.75rem"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
        >
          <Text fontSize="lg" fontWeight="600" mb="1rem">
            {RESOURCE_GRID_COPY.bulkEditor.changesToApply}
          </Text>

          <Stack spacing="1rem">
            <Flex gap="1rem" align="center">
              <Text minW="150px" fontSize="sm" fontWeight="500">
                Status:
              </Text>
              {(() => {
                const currentStatus = bulkEditState.combinedStatus || '';
                const currentStatusConfig = currentStatus
                  ? getStatusConfigForOption(currentStatus)
                  : null;

                return (
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="md"
                      minW="250px"
                      height="2.75rem"
                      bg={currentStatusConfig?.bgColor || 'gray.200'}
                      color={currentStatusConfig ? 'white' : 'gray.700'}
                      borderRadius="0.3125rem"
                      fontWeight="600"
                      fontSize="0.75rem"
                      textAlign="left"
                      _hover={{
                        filter: currentStatusConfig
                          ? 'brightness(1.05)'
                          : undefined,
                      }}
                      _active={{
                        filter: currentStatusConfig
                          ? 'brightness(0.95)'
                          : undefined,
                      }}
                      rightIcon={
                        <Box
                          as="svg"
                          width="12px"
                          height="12px"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </Box>
                      }
                    >
                      <Text noOfLines={1} textAlign="left" width="100%">
                        {currentStatusConfig?.label || 'Select status'}
                      </Text>
                    </MenuButton>
                    <MenuList
                      minW="300px"
                      maxW="350px"
                      p={2}
                      borderRadius="0.5rem"
                      boxShadow="lg"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      {COMBINED_STATUS_OPTIONS.map((option) => {
                        const optionConfig = getStatusConfigForOption(option);
                        const isSelected = option === currentStatus;

                        return (
                          <MenuItem
                            key={option}
                            onClick={() =>
                              updateBulkEditState('combinedStatus', option)
                            }
                            bg={
                              isSelected ? 'blue.50' : 'transparent'
                            }
                            _hover={{
                              bg: isSelected ? 'blue.100' : 'gray.50',
                            }}
                            borderRadius="0.375rem"
                            mb={1}
                            p={2}
                            minH="auto"
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap="0.75rem"
                              width="100%"
                            >
                              <Box
                                width="1rem"
                                height="1rem"
                                borderRadius="0.25rem"
                                bg={optionConfig.bgColor}
                                flexShrink={0}
                              />
                              <Box flex="1">
                                <Text
                                  fontSize="0.875rem"
                                  fontWeight={isSelected ? '600' : '500'}
                                  color="gray.800"
                                  mb={0.5}
                                >
                                  {optionConfig.label}
                                </Text>
                                <Text
                                  fontSize="0.75rem"
                                  color="gray.600"
                                  lineHeight="1.2"
                                >
                                  {optionConfig.helperText.split(' | ')[0]}
                                </Text>
                              </Box>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Menu>
                );
              })()}
            </Flex>

            <Flex gap="1rem" align="center">
              <AgeRangeSelect
                value={bulkEditState.ageRange ? labelToRange(bulkEditState.ageRange) : { min: null, max: null }}
                onChange={(_, label) => updateBulkEditState('ageRange', label)}
                placeholder="Select age range"
                maxWidth="200px"
                data-testid="bulk-edit-age-range"
                label="Age Range"
              />
            </Flex>

          </Stack>
        </Box>

        {/* Plans Table */}
        <Box
          p="1.5rem"
          bg="white"
          borderRadius="0.75rem"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
        >
          <Flex justify="space-between" align="center" mb="1rem">
            <Text fontSize="lg" fontWeight="600">
              Plans to Edit ({selectedRows.size} of {plans.length} selected)
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={toggleAllRows}
            >
              {selectedRows.size === plans.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Flex>

          {loading ? (
            <Text>Loading plans...</Text>
          ) : (
            <TableContainer maxH="400px" overflowY="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th w="50px">
                      <Checkbox
                        isChecked={selectedRows.size === plans.length && plans.length > 0}
                        isIndeterminate={selectedRows.size > 0 && selectedRows.size < plans.length}
                        onChange={toggleAllRows}
                      />
                    </Th>
                    <Th>Provider</Th>
                    <Th>Practice</Th>
                    <Th>Status</Th>
                    <Th>Age Range</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {plans.map((plan) => {
                    const rowId = `${plan.provider.id}-${plan.practice.id}`;
                    const firstPlan = plan.plans[0];

                    // Reconstruct age range for display
                    const ageRange = firstPlan
                      ? reconstructAgeRange({
                          minimumAge: firstPlan.minimumAge,
                          maximumAge: firstPlan.maximumAge,
                          age: firstPlan.age,
                          metadata: firstPlan.metadata as
                            | {
                                minimumAge?: number | null;
                                maximumAge?: number | null;
                              }
                            | undefined,
                        })
                      : { min: null, max: null };
                    const ageRangeLabel = rangeToLabel(ageRange);

                    return (
                      <Tr key={rowId}>
                        <Td>
                          <Checkbox
                            isChecked={selectedRows.has(rowId)}
                            onChange={() => toggleRow(rowId)}
                          />
                        </Td>
                        <Td>
                          <Tooltip
                            label="Open provider detail page in new tab"
                            placement="top"
                            hasArrow
                          >
                            <Link
                              href={`/providers/${plan.provider.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="blue.600"
                              fontWeight="500"
                              _hover={{
                                color: 'blue.700',
                                textDecoration: 'underline',
                              }}
                            >
                              {plan.provider.firstName}{' '}
                              {plan.provider.lastName}
                              {plan.provider.suffix &&
                                `, ${plan.provider.suffix}`}
                            </Link>
                          </Tooltip>
                        </Td>
                        <Td>
                          <Tooltip
                            label="Open practice detail page in new tab"
                            placement="top"
                            hasArrow
                          >
                            <Link
                              href={`/practices/${plan.practice.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="blue.600"
                              fontWeight="500"
                              _hover={{
                                color: 'blue.700',
                                textDecoration: 'underline',
                              }}
                            >
                              {plan.practice.name}
                            </Link>
                          </Tooltip>
                        </Td>
                        <Td>
                          {(() => {
                            const currentStatus =
                              getCombinedStatusFromFields(
                                firstPlan?.credentialstatus || 'Credentialed',
                                firstPlan?.value || 'No Status'
                              );
                            const statusConfig =
                              getStatusConfigForOption(currentStatus);

                            return (
                              <Tooltip
                                label={statusConfig.helperText}
                                hasArrow
                                placement="top"
                                maxW="25rem"
                              >
                                <Box
                                  display="flex"
                                  height="3.5rem"
                                  padding="1rem"
                                  justifyContent="center"
                                  alignItems="center"
                                  gap="0.25rem"
                                  width="100%"
                                  maxWidth="12rem"
                                  borderRadius="0.3125rem"
                                  background={statusConfig.bgColor}
                                  cursor="help"
                                  transition="all 0.2s ease-in-out"
                                  flexDirection="column"
                                  flexShrink={0}
                                  overflow="hidden"
                                  position="relative"
                                >
                                  <Text
                                    fontSize="0.75rem"
                                    fontWeight="600"
                                    lineHeight="1.1"
                                    color="white"
                                    textAlign="left"
                                    whiteSpace="normal"
                                    wordBreak="break-word"
                                    width="100%"
                                    maxW="100%"
                                    flex="0 0 auto"
                                  >
                                    {statusConfig.label}
                                  </Text>
                                  <Text
                                    fontSize="0.625rem"
                                    fontWeight="400"
                                    lineHeight="1.0"
                                    color="white"
                                    textAlign="left"
                                    opacity="0.9"
                                    whiteSpace="normal"
                                    wordBreak="break-word"
                                    width="100%"
                                    maxW="100%"
                                    flex="0 0 auto"
                                  >
                                    {statusConfig.helperText.split(' | ')[0]}
                                  </Text>
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </Td>
                        <Td>
                          <Text fontSize="0.875rem" color="gray.700">
                            {ageRangeLabel}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Action Buttons */}
        <Flex justify="flex-end" gap="1rem">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            isDisabled={processing}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={processBulkUpdate}
            isDisabled={processing || selectedRows.size === 0 || Object.keys(bulkEditState).length === 0}
            isLoading={processing}
            loadingText="Processing..."
          >
            {RESOURCE_GRID_COPY.bulkEditor.saveChanges}
          </Button>
        </Flex>

        {/* Progress */}
        {processing && (
          <Box
            p="1.5rem"
            bg="white"
            borderRadius="0.75rem"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
          >
            <Text mb="1rem">
              {RESOURCE_GRID_COPY.bulkEditor.progress
                .replace('{current}', progress.current.toString())
                .replace('{total}', progress.total.toString())}
            </Text>
            <Progress value={(progress.current / progress.total) * 100} />
          </Box>
        )}
      </Stack>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Bulk Update</ModalHeader>
          <ModalBody>
            <Text mb="1rem">
              You are about to update {selectedRows.size} insurance plan{selectedRows.size !== 1 ? 's' : ''}.
            </Text>
            <Text fontSize="sm" color="gray.600">
              This action cannot be undone. Please review your changes before proceeding.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onConfirmClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={performBulkUpdate}>
              Confirm Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Plan Selection Modal */}
      <BulkEditModal
        isOpen={isPlanSelectionOpen}
        onClose={onPlanSelectionClose}
        onConfirm={handlePlanSelectionConfirm}
        plans={plans}
        selectedRows={selectedRows}
        context={{
          scopeType: 'market',
          scopeId: 'all',
          activeFilters: filters,
        }}
      />
    </Box>
  );
};

export { BulkEditPlans };
